import { Button, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuTrigger } from '@1trading/react';
import { useQueryClient } from '@tanstack/react-query';
import { ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { confirmAction } from '@/components/dialogs/confirm-action';
import { orderStatusActions, STATUS_ACTION_DESCRIPTIONS, STATUS_LABELS, type SurveillanceStatusAction, statusDotClass } from '@/features/market-surveillance/surveillance-meta';
import { useChangeAlertStatus } from '@/hooks/queries/market-surveillance/use-surveillance-alert';
import { SURVEILLANCE_ALERT_QUERY_KEY } from '@/hooks/queries/market-surveillance/use-surveillance-alerts';
import { getErrorMessage, HTTP_BAD_REQUEST, HTTP_CONFLICT, isHttpStatus } from '@/lib/api/error';
import type { SurveillanceAlertDetail } from '@/services/api/market-surveillance';

interface AlertStatusActionsProps {
  alertId: string;
  detail: SurveillanceAlertDetail;
}

export const AlertStatusActions = ({ alertId, detail }: AlertStatusActionsProps) => {
  const queryClient = useQueryClient();
  const changeStatus = useChangeAlertStatus(alertId);

  // Server-driven: only the transitions bop-api allows, in the fixed display order
  const actions = orderStatusActions(detail.allowedTransitions);
  if (!detail.canEdit || actions.length === 0) return null;

  const handleAction = async (action: SurveillanceStatusAction) => {
    const justification = await confirmAction({
      title: `Set status to ${action.label}?`,
      description: `${STATUS_LABELS[detail.status.status]} → ${action.label}. The assessment below is recorded in the audit trail.`,
      actionLabel: action.label,
      // Never destructive-styled: recording a review verdict is the modal's PRIMARY action
    });
    if (justification === null) return;

    try {
      await changeStatus({ status: action.status, assessment: justification });
      toast.success(`Status set to ${action.label}`);
    } catch (error) {
      if (isHttpStatus(error, HTTP_CONFLICT)) {
        // REVIEW_IN_PROGRESS — someone else is mid-review; retryable
        toast.error('Another review of this alert is in progress — retry shortly.');
        return;
      }
      if (isHttpStatus(error, HTTP_BAD_REQUEST)) {
        // ILLEGAL_TRANSITION — our allowed_transitions were stale; refetch the truth
        toast.error('That transition is no longer allowed — the alert has moved on. Refreshing.');
        await queryClient.invalidateQueries({ queryKey: [...SURVEILLANCE_ALERT_QUERY_KEY, alertId] });
        return;
      }
      toast.error(`Failed to change status: ${getErrorMessage(error)}`);
    }
  };

  // One labelled affordance instead of a row of coloured pills: colour marks the TARGET
  // status (same palette as the chips), never button danger, and each item says what the
  // transition means. The justification dialog still guards the commitment.
  return (
    // Wrapper stops the stretch-aligned page column from inflating the trigger to full width
    <div className="flex">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button size="sm" type="button" variant="primary">
            Update status
            <ChevronDown aria-hidden className="ml-1.5 size-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-80">
          <DropdownMenuLabel className="text-xs text-muted-foreground uppercase tracking-wide">Move alert to</DropdownMenuLabel>
          {actions.map((action) => (
            <DropdownMenuItem className="cursor-pointer items-start gap-3 py-2" key={action.status} onSelect={() => handleAction(action)}>
              <span aria-hidden className={`mt-1.5 size-2 shrink-0 rounded-full ${statusDotClass(action.status)}`} />
              <span className="flex flex-col gap-0.5">
                <span className="font-medium leading-none">{action.label}</span>
                <span className="text-xs text-muted-foreground">{STATUS_ACTION_DESCRIPTIONS[action.status]}</span>
              </span>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};
AlertStatusActions.displayName = 'AlertStatusActions';
